// traceProvenance.js — deterministic, LLM-free provenance between the artifacts
// the X-ray libs reconstruct. It answers "what fed into this, and what did this
// feed?" purely from evidence in the traces — no model, no inference.
//
// Nodes come from the sibling extractors' flat op streams (traceWebResearch,
// traceLinearOps, traceFileTree), each already keyed by (session, ts,
// endpoint). Edges are computed PAIRWISE within a session, source-earlier →
// target-later, so both directions fall out of one mechanism (a Linear ticket
// read before a file write ⇒ ticket→code; a file written before a ticket is
// updated ⇒ code→ticket).
//
// Two honestly-labelled edge classes, because they are different kinds of claim:
//
//   • derivation — EVIDENCE that the target was built from the source:
//       cited   an identity (url / issue-id / path) transferred between them;
//       quoted  a rare verbatim span (k-gram) appears in both;
//       entity  a rare distinctive token (symbol / path / url / id) is shared.
//     High precision. This is the defensible half.
//
//   • consultation — a research node (web fetch/search) was consumed before a
//     production, with no textual transfer. Research shapes code by forming
//     intent, not by being quoted, so this is the whole point of the research
//     surface — but it is CORRELATIONAL, not proof. It carries the source's
//     stated intent (the search query / fetch prompt) and a temporal-proximity
//     score, and is labelled "consulted before", never "derived from".
//
// Scope is WITHIN a session — the slice where temporal order plus textual
// transfer carry real weight.
//
// Linking sessions to each other on a shared identifier deliberately does NOT
// belong here. That is a longitudinal question ("what else has ever touched
// this"), while a scan window is a minutes-wide cross-section of concurrent
// sessions and so cannot hold both ends of such a link. It wants a corpus-wide
// index queried from one artifact outward, the way RelatedWorkPanel does it —
// not a scan for coincidences inside a sample.

// Bounds — this is a client pass over a scan window; keep it cheap and honest
// about what it drops.
const TEXT_SCAN_CAP = 6000; // chars of a node's text scanned for overlap
const SHINGLE_K = 8; // words per verbatim-overlap shingle
const DISTINCTIVE_DF = 6; // an entity in > this many nodes is boilerplate, ignored
const MAX_EDGES_PER_TARGET = 12; // ranked, then capped
const MAX_CONSULT_PER_TARGET = 6; // nearest research consultations kept

// ── node adapters ────────────────────────────────────────────────────────────
// Each maps one lib's flat ops (post collectWindow*) to provenance nodes.
// role is advisory (for labels); edges are computed by temporal order, so a
// "production" can still be a source for something later (code → ticket).

function nodeOf(kind, role, ref, label, text, intent, op) {
  return {
    id: `${kind}:${op.sessionId || ""}:${op.promptId || ""}:${op.key ?? ""}`,
    // The extractor's own op key, carried rather than left to be parsed back
    // out of the id: it is the handle every other reading of the same trace
    // indexes an op by (the semantic feed keys its items on it), so a consumer
    // that wants to line a provenance edge up with those readings needs it as
    // a field, not as the fourth colon-separated segment of a string.
    key: op.key ?? null,
    kind, role,
    ref: ref || null,
    label,
    text: typeof text === "string" ? text : "",
    intent: intent || null,
    sessionId: op.sessionId || "",
    endpointId: op.endpointId || "",
    endpointName: op.endpointName || "",
    ts: op.ts ?? null,
    promptId: op.promptId || null,
  };
}

export function webResearchNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok) continue;
    if (op.kind === "fetch") {
      if (!op.url) continue;
      out.push(nodeOf("web.fetch", "input", op.url, `Fetched ${op.host || op.url}`, op.content || "", op.prompt || null, op));
    } else if (op.kind === "search") {
      const text = [op.query, ...(op.results || []).map((r) => r.title || r.url)].filter(Boolean).join("\n");
      out.push(nodeOf("web.search", "input", null, `Searched “${op.query || ""}”`, text, op.query || null, op));
    }
  }
  return out;
}

export function linearNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok || !op.issue) continue;
    if (op.kind === "write") {
      out.push(nodeOf("linear.write", "output", op.issue, `${op.action || "wrote"} ${op.issue}`, op.content || "", null, op));
    } else if (op.kind === "read") {
      out.push(nodeOf("linear.read", "input", op.issue, `Read ${op.issue}`, op.content || "", null, op));
    }
    // browse: no specific artifact identity — not a provenance node.
  }
  return out;
}

export function fileNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok || !op.path) continue;
    if (op.kind === "write") {
      out.push(nodeOf("file.write", "output", op.path, `Wrote ${op.path}`, op.content || "", null, op));
    } else if (op.kind === "edit") {
      const text = (op.hunks || []).map((h) => h?.new || "").filter(Boolean).join("\n");
      out.push(nodeOf("file.edit", "output", op.path, `Edited ${op.path}`, text, null, op));
    } else if (op.kind === "read") {
      const text = (op.obs || []).map((o) => (Array.isArray(o) ? o[1] : "")).join("\n");
      out.push(nodeOf("file.read", "input", op.path, `Read ${op.path}`, text, null, op));
    }
    // delete: no content to attribute.
  }
  return out;
}

// Forge work driven through the shell (PRs, issues, CI). A read carries what
// the command SHOWED the agent; a write carries what it sent. `browse` has no
// artifact identity — not a node, same rule as a Linear browse.
export function forgeNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok) continue;
    const ref = forgeRef(op);
    if (!ref) continue;
    const observed = op.content || op.observedBody || op.observedText || op.diff || op.log || "";
    if (op.kind === "write" || op.kind === "push" || op.create) {
      out.push(nodeOf("forge.write", "output", ref, `${op.create ? "created" : op.action || "wrote"} ${ref}`, observed, op.title || null, op));
    } else if (op.kind === "read") {
      out.push(nodeOf("forge.read", "input", ref, `Read ${ref}`, observed, null, op));
    }
  }
  return out;
}

function forgeRef(op) {
  if (op.num) return `${op.repo || "?"}#${op.num}`;
  return op.repo || null;
}

// Data the agent QUERIED — analytics/emissary MCP, SQL through the shell, and
// Grafana telemetry. These behave like research: they form intent rather than
// being quoted, so a query before a production is a CONSULTATION (see
// isConsultative), not a derivation, unless text actually transfers.
export function queryNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok) continue;
    if (op.kind === "browse") continue; // navigation, no artifact identity
    const ref = op.engine ? `${op.engine}:${op.target || "query"}` : null;
    const text = [op.query, op.content, op.result?.text].filter(Boolean).join("\n");
    if (op.kind === "write") {
      out.push(nodeOf("query.write", "output", ref, `${op.engine} write → ${op.target || ""}`, text, op.query || null, op));
    } else {
      out.push(nodeOf("query.run", "input", ref, `${op.engine} → ${op.target || "query"}`, text, op.query || null, op));
    }
  }
  return out;
}

// The agent consulting Origin itself. Uniquely, a recall op names the PRIOR
// SESSIONS it retrieved — recorded by the server, not inferred — so these nodes
// carry `recalls`, the only ground-truth cross-session edge in the graph.
export function recallNodes(flatOps) {
  const out = [];
  for (const op of flatOps || []) {
    if (!op.ok || op.kind !== "search") continue;
    const cands = op.view?.candidates || [];
    const text = [
      op.request,
      ...(op.facets || []),
      ...(op.view?.topics || []).map((t) => t.name),
      ...cands.map((c) => c.summary),
    ].filter(Boolean).join("\n");
    const n = nodeOf("recall.search", "input", null, `Recalled “${clipLabel(op.request)}”`, text, op.request || null, op);
    n.recalls = [...new Set(cands.map((c) => c.sessionId).filter(Boolean))];
    out.push(n);
  }
  return out;
}

// A published artifact — a production with a durable external identity.
export function artifactNodes(flatMarks) {
  const out = [];
  for (const m of flatMarks || []) {
    const p = m?.publish;
    if (!p) continue;
    const ref = p.url || p.path || null;
    if (!ref) continue;
    const text = [p.title, p.description].filter(Boolean).join("\n");
    out.push(nodeOf("artifact.publish", "output", ref, `Published ${p.title || p.path || p.url}`, text, null, m));
  }
  return out;
}

// The X-ray factory's lenses (dashboard/xray-factory/), projected into nodes.
//
// Generic on purpose. Which lenses contribute, and how each maps an op, is
// DECLARED by the lens in its own manifest (`lineage` in xrays/<id>.xray.js) and
// handed here by the registry's lineageSources(). This adapter used to carry a
// hand-picked list of six, which was this file pre-deciding what a lens is for on
// the lens's behalf; a lens that contributes nothing now records why in its own
// `lineageReason` instead.
//
// The projection is functions of an op rather than field names because the
// identity is usually derived — a service in its environment, a package in its
// registry — and because only the lens knows which of its own kinds changed
// something.

export function factoryNodes(sources, opsByLens) {
  const out = [];
  const byLens = opsByLens || {};
  for (const src of sources || []) {
    const lin = src?.lineage;
    if (!lin) continue;
    for (const op of byLens[src.id] || []) {
      // PER OP, because these five functions are lens-authored and run against
      // whatever real traces contain, while the manifest contract only checks
      // that they ARE functions. An op shape the lens did not anticipate must
      // cost that one node, not the graph — and not the view, since this runs
      // inside a render memo with no boundary between here and the app root.
      try {
        const ref = lin.ref(op) || null;
        const text = lin.text(op) || "";
        // THE FLOOR, and it is weaker than "must have a ref". Citation needs an
        // identity, but the entity and shingle tiers do not — they match on prose.
        // What can never link, in any tier, is a node with NEITHER, and that is the
        // only thing worth refusing: it can do nothing but inflate the census.
        if (!ref && !text) continue;
        out.push({
          ...nodeOf(lin.kind, lin.role(op), ref, lin.label(op), text, lin.intent ? lin.intent(op) : null, op),
          // Two facts the host must NOT re-derive from the kind. `fromLens` is
          // what lets `role: "production"` be believed without a list of which
          // kinds are allowed to claim it; `citable` is the lens's statement that
          // its ref is a coordinate. Both travel on the node because that is where
          // the matchers can see them.
          fromLens: true,
          citable: Boolean(lin.citable),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[xray] ${src.id} lineage projection threw`, err);
      }
    }
  }
  return out;
}

function clipLabel(s) {
  const t = String(s || "");
  return t.length > 60 ? `${t.slice(0, 59)}…` : t;
}

// ── text / entity primitives ─────────────────────────────────────────────────

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const ISSUE_ID_RE = /\b([A-Z][A-Z0-9]{0,9}-\d{1,6})\b/g;
const URL_RE = /https?:\/\/[^\s"'<>)\]}]+/g;
// A path-ish token: has a slash and a dotted filename at the end.
const PATH_RE = /\b[\w./-]*\/[\w-]+\.\w{1,6}\b/g;
// A distinctive code identifier: camelCase or snake_case, length ≥ 6. Plain
// all-lowercase words are excluded — they're the boilerplate that pollutes.
const SYM_RE = /\b([A-Za-z_][A-Za-z0-9_]{5,})\b/g;

// The distinctive entities in a text — the tokens rare/structured enough that a
// coincidental match across two unrelated artifacts is unlikely.
export function extractEntities(text) {
  const out = new Set();
  const s = typeof text === "string" ? text.slice(0, TEXT_SCAN_CAP) : "";
  if (!s) return out;
  for (const m of s.matchAll(ISSUE_ID_RE)) out.add(`id:${m[1].toUpperCase()}`);
  for (const m of s.matchAll(URL_RE)) out.add(`url:${stripScheme(m[0])}`);
  for (const m of s.matchAll(PATH_RE)) out.add(`path:${m[0]}`);
  for (const m of s.matchAll(SYM_RE)) {
    const t = m[1];
    if (/[a-z][A-Z]/.test(t) || t.includes("_")) out.add(`sym:${t}`); // camelCase or snake_case only
  }
  return out;
}

function stripScheme(u) {
  return String(u).replace(/^https?:\/\//, "").replace(/[.,;]+$/, "").slice(0, 160);
}

// Tokenize keeping each token's ORIGINAL offset, so a matched shingle can be
// shown as the source text it actually covers — "http://forgejo-http.forgejo
// .svc.cluster.local:3000", not the punctuation-stripped token run
// "http forgejo http forgejo svc cluster local 3000" (which reads as jibberish).
function tokensWithSpans(text) {
  const s = typeof text === "string" ? text.slice(0, TEXT_SCAN_CAP) : "";
  const toks = [];
  const re = /[a-z0-9]+/gi;
  let m;
  while ((m = re.exec(s)) !== null) toks.push({ w: m[0].toLowerCase(), start: m.index, end: m.index + m[0].length });
  return { s, toks };
}

function shingleKeys(text) {
  const { toks } = tokensWithSpans(text);
  const set = new Set();
  for (let i = 0; i + SHINGLE_K <= toks.length; i++) set.add(toks.slice(i, i + SHINGLE_K).map((t) => t.w).join(" "));
  return set;
}

// ── matchers ─────────────────────────────────────────────────────────────────

const MIN_CITABLE_REF = 8;
// Splits a coordinate into the parts that make it hierarchical.
const REF_SEGMENT_RE = /[/:@]+/;

// Is a ref precise enough to hang a derivation claim on? Same rule the forge case
// already states — a bare "#386" is too common in prose to be evidence — applied
// to every ref the graph learns to cite: ONE COMMON TOKEN found in later prose is
// a coincidence, not a transfer. So a ref qualifies only when it names its target
// hierarchically — a separator, two segments of real length, and enough total
// length that an accidental match is implausible. `prod/prod-backend/skwach`
// qualifies; the same lens's bare `skwach` does not, and neither does a keychain
// entry called `forgejo`.
export function identityShapedRef(ref) {
  const s = typeof ref === "string" ? ref : "";
  if (s.length < MIN_CITABLE_REF || !s.includes("/")) return false;
  return s.split(REF_SEGMENT_RE).filter((p) => p.length >= 2).length >= 2;
}

// Did an identity transfer between the two nodes (either direction)? The
// derived artifact naming the source's id, OR the source naming the derived
// artifact's id (a ticket that named the file that then got written) — both are
// hard evidence of a link.
function citation(a, b) {
  const hit = (n, text) => {
    const { ref, kind } = n;
    if (!ref || !text) return false;
    if (kind === "web.fetch") { const s = stripScheme(ref); return s.length >= 8 && text.includes(s); }
    if (kind.startsWith("linear")) return new RegExp(`\\b${escapeRe(ref)}\\b`, "i").test(text);
    if (kind.startsWith("file")) return ref.length >= 6 && text.includes(ref);
    // A forge ref is "<repo>#<num>". Require the WHOLE thing: a bare "#386"
    // is far too common in prose to carry a derivation claim on its own, and
    // a weaker overlap still reaches the entity tier.
    if (kind.startsWith("forge")) return ref.includes("#") && text.includes(ref);
    // An artifact's identity is a url or a path, depending on which it has.
    if (kind === "artifact.publish") {
      const s = /^https?:\/\//.test(ref) ? stripScheme(ref) : ref;
      return s.length >= 8 && text.includes(s);
    }
    // A lens-declared node cites only when its own manifest says its ref is a
    // coordinate — a remote prefix, a service in its environment, a stack path —
    // and never on the kind, which the host used to keep a list of. That list
    // meant a new lens could not become citable by being right, only by someone
    // remembering to edit this file. The lens knows the shape of the ref it
    // resolves; the host knows what "precise enough" means. Each states its half.
    //
    // Still gated on the ref ACTUALLY being one, because a lens that declares
    // `citable` can also resolve a bare name for some ops — a service with no
    // namespace, a keychain entry — and that half is no more citable than "#386".
    if (n.citable) return identityShapedRef(ref) && text.includes(ref);
    // A lens that declares nothing is not citable. A unit name is routinely a
    // bare common word (`forgejo`) and a package name is worse; a prose match
    // there manufactures a derivation claim rather than finding one. Those ops
    // still reach the quoted and entity tiers through their text, which is where
    // a weaker signal belongs.
    //
    // query.* refs are "<engine>:<target>" — a schema coordinate, not an
    // artifact identity, so they don't support citation. Entity tier handles it.
    return false;
  };
  if (hit(a, b.text)) return a.ref; // b names a
  if (hit(b, a.text)) return b.ref; // a named b
  return null;
}

// A URL or filesystem path is a shared IDENTIFIER, not copied PROSE — it belongs
// to the citation / entity tiers (which render it cleanly as "shares <url>"),
// not "quoted". A shingle whose span is one reads as a ragged, punctuation-noisy
// fragment ("URL**: `http://forgejo-http.forgejo.svc.cluster.local"), so skip it
// and let a genuine prose overlap — or, failing that, the entity tier — win.
const IDENTIFIERISH = /:\/\/|[\w.]\/[\w.]/;

// A rare verbatim PROSE overlap — returned as the SOURCE's ORIGINAL text span
// (the readable evidence a consumer shows), keyed on the normalized token shingle.
//
// Tokenizing is the pass's real cost, and the pair loop visits every node
// O(n) times — so both sides memoize per NODE per run (the caches argument),
// not per pair. Before this, a production-heavy window re-tokenized up to
// 6000 chars twice for every pair: ~10s on the main thread at 300 nodes.
function sourceShingles(a, caches) {
  let cached = caches.src.get(a.id);
  if (cached) return cached;
  const { s, toks } = tokensWithSpans(a.text);
  const spans = [];
  for (let i = 0; i + SHINGLE_K <= toks.length; i++) {
    const span = s.slice(toks[i].start, toks[i + SHINGLE_K - 1].end);
    // An identifier-ish span can never be returned as evidence (the match
    // loop always skipped it), so it earns no place in the scan list or the
    // candidate index — recurring URL/path boilerplate otherwise forces a
    // full fruitless scan on every pair that shares it.
    if (IDENTIFIERISH.test(span)) continue;
    spans.push({
      key: toks.slice(i, i + SHINGLE_K).map((t) => t.w).join(" "),
      span,
    });
  }
  caches.src.set(a.id, spans);
  return spans;
}

function sharedShingle(a, b, caches) {
  const spans = sourceShingles(a, caches);
  if (!spans.length) return null;
  let bkeys = caches.tgt.get(b.id);
  if (!bkeys) {
    bkeys = shingleKeys(b.text);
    caches.tgt.set(b.id, bkeys);
  }
  for (const { key, span } of spans) {
    if (!bkeys.has(key)) continue;
    return span;
  }
  return null;
}

function rareSharedEntities(aEnt, bEnt, df) {
  const shared = [];
  const [small, big] = aEnt.size <= bEnt.size ? [aEnt, bEnt] : [bEnt, aEnt];
  for (const e of small) {
    if (big.has(e) && (df.get(e) || 0) <= DISTINCTIVE_DF) shared.push(e);
  }
  // Rarest first — the strongest evidence leads.
  shared.sort((x, y) => (df.get(x) || 0) - (df.get(y) || 0));
  return shared;
}

// ── the pass ───────────────────────────────────────────────────────────────

// buildProvenance(nodes) → { edges, byTarget, bySource, stats }
//   edge: { source, target, direction, class, tier, evidence[], score, sessionId }
//   where source/target are compact { id, kind, ref, label, ts, endpointName }.
export function buildProvenance(nodes, opts = {}) {
  const list = (nodes || []).filter((n) => n && n.id && n.sessionId);
  const entities = new Map(); // id → Set
  const df = new Map(); // entity → node count
  for (const n of list) {
    const e = extractEntities(n.text);
    entities.set(n.id, e);
    for (const k of e) df.set(k, (df.get(k) || 0) + 1);
  }

  // Session → nodes, ordered by (ts, arrival). Arrival index breaks ties so a
  // same-timestamp read-then-write keeps its authored order.
  const bySession = new Map();
  list.forEach((n, i) => {
    const arr = bySession.get(n.sessionId) || [];
    arr.push({ n, i });
    bySession.set(n.sessionId, arr);
  });

  const shingleCaches = { src: new Map(), tgt: new Map() };
  // Inverted shingle index: a target only pays the span scan for sources
  // that share at least one shingle key. Without this, fully-distinct texts
  // are the WORST case — every pair scans every cached shingle to conclude
  // nothing (measured ~1s for zero edges at 300 nodes).
  const targetKeys = (n) => {
    let k = shingleCaches.tgt.get(n.id);
    if (!k) { k = shingleKeys(n.text); shingleCaches.tgt.set(n.id, k); }
    return k;
  };
  // The index holds only keys a node could USABLY source (identifier-ish
  // spans are filtered above), so pairs whose only overlap is boilerplate
  // identifiers never scan at all.
  const keyOwners = new Map();
  for (const n of list) {
    const seen = new Set();
    for (const { key } of sourceShingles(n, shingleCaches)) {
      if (seen.has(key)) continue;
      seen.add(key);
      let owners = keyOwners.get(key);
      if (!owners) { owners = []; keyOwners.set(key, owners); }
      owners.push(n.id);
    }
  }
  // Keys shared by MANY nodes are boilerplate (recurring tool-schema or
  // system-prompt text). Unioning their owner lists per target is its own
  // quadratic, so they're tracked per NODE instead: a pair still scans when
  // it shares any boilerplate key, via a small pairwise set intersection —
  // never by abandoning the filter for the whole target.
  //
  // Above this owner count a key is treated as boilerplate: unioning its
  // owner list into per-target candidate sets costs more than the scans it
  // would skip, because a scan against a co-owner is guaranteed to match and
  // early-return. Sits deliberately near DISTINCTIVE_DF (6) so "too common
  // to be distinctive" means roughly the same thing across the entity and
  // quoted tiers.
  //
  // Aggregate cost, not just per-key: sharesCommonKey runs per candidate
  // pair and costs O(min set size) with early exit on the first shared key —
  // co-clique pairs hit immediately; the worst case is DISJOINT boilerplate
  // cliques, where cross-clique pairs pay a full intersection to conclude
  // nothing, still strictly cheaper than the span scan it gates. Measured at
  // the op cap in scripts/trace-anatomy-bench.mjs (multi-clique profile,
  // ~530ms alongside the other sub-second worst cases).
  const COMMON_OWNERS = 8;
  const commonKeysOf = new Map();
  for (const [key, owners] of keyOwners) {
    if (owners.length <= COMMON_OWNERS) continue;
    for (const id of owners) {
      let set = commonKeysOf.get(id);
      if (!set) { set = new Set(); commonKeysOf.set(id, set); }
      set.add(key);
    }
  }
  // Clique-shaped sessions give many nodes the same common-key set (the
  // whole point of boilerplate). Intern content-identical sets and memoize
  // intersections per set-PAIR, so the pairwise cost is paid once per
  // distinct clique pair, not once per node pair — at the shingle ceiling
  // (~1,200 keys/node, 10 cliques, 300 ops) this is the difference between
  // ~45k full intersections and ~45.
  const internedSets = new Map();
  for (const [id, set] of commonKeysOf) {
    const canonical = [...set].sort().join("\u0000");
    let shared = internedSets.get(canonical);
    if (!shared) { shared = set; internedSets.set(canonical, shared); }
    commonKeysOf.set(id, shared);
  }
  const pairMemo = new Map(); // interned set → Map(interned set → boolean)
  const sharesCommonKey = (aId, bId) => {
    const as = commonKeysOf.get(aId);
    const bs = commonKeysOf.get(bId);
    if (!as || !bs) return false;
    if (as === bs) return true; // same interned set — sets are never empty
    const hit = pairMemo.get(as)?.get(bs);
    if (hit !== undefined) return hit;
    const [small, big] = as.size <= bs.size ? [as, bs] : [bs, as];
    let found = false;
    for (const k of small) if (big.has(k)) { found = true; break; }
    for (const [x, y] of [[as, bs], [bs, as]]) {
      let memo = pairMemo.get(x);
      if (!memo) { memo = new Map(); pairMemo.set(x, memo); }
      memo.set(y, found);
    }
    return found;
  };
  const edges = [];
  for (const arr of bySession.values()) {
    arr.sort((p, q) => (tsOf(p.n.ts) - tsOf(q.n.ts)) || (p.i - q.i));
    const seq = arr.map((x) => x.n);
    for (let bi = 0; bi < seq.length; bi++) {
      const b = seq[bi]; // target (later)
      // Provenance is about what was PRODUCED and what informed it. A read (or a
      // search/fetch) as the target produced nothing, so it can't "derive from"
      // anything — only productions are valid targets. This is what keeps the
      // graph from flooding with read↔read pairs that merely share a codebase's
      // symbols. Reads/fetches still participate freely as SOURCES.
      if (!isProduction(b)) continue;
      const shingleCandidates = new Set();
      for (const key of targetKeys(b)) {
        const owners = keyOwners.get(key);
        if (!owners || owners.length < 2 || owners.length > COMMON_OWNERS) continue;
        for (const id of owners) shingleCandidates.add(id);
      }
      const consult = [];
      for (let ai = 0; ai < bi; ai++) {
        const a = seq[ai]; // source (earlier)
        if (a.id === b.id) continue;
        // Same file read→write of itself is not provenance, it's the same doc.
        if (a.kind.startsWith("file") && b.kind.startsWith("file") && a.ref && a.ref === b.ref) continue;
        // The same rule one domain over, and it only bites now that a factory ref
        // is citable: a transfer's own output names its location, so two uploads
        // to one prefix would each cite the last. One identity is one target, not
        // one thing feeding another.
        if (a.kind === b.kind && a.citable && a.ref && a.ref === b.ref) continue;

        const cite = citation(a, b);
        if (cite) { edges.push(edge(a, b, "derivation", "cited", [cite], 1.0)); continue; }

        const sh = (shingleCandidates.has(a.id) || sharesCommonKey(a.id, b.id)) ? sharedShingle(a, b, shingleCaches) : null;
        if (sh) { edges.push(edge(a, b, "derivation", "quoted", [sh], 0.8)); continue; }

        const shared = rareSharedEntities(entities.get(a.id), entities.get(b.id), df);
        if (shared.length) {
          const weight = shared.reduce((s, e) => s + 1 / (df.get(e) || 1), 0);
          edges.push(edge(a, b, "derivation", "entity", shared.slice(0, 5).map(pretty), Math.min(0.7, 0.35 + weight * 0.1)));
          continue;
        }

        // No textual evidence. If the source is one of the intent-forming
        // kinds, keep it as a consultation candidate (ranked by proximity).
        if (isConsultative(a)) {
          consult.push({ a, gap: bi - ai });
        }
      }
      consult.sort((x, y) => x.gap - y.gap);
      for (const c of consult.slice(0, MAX_CONSULT_PER_TARGET)) {
        const ev = c.a.intent ? [c.a.intent] : [c.a.label];
        edges.push(edge(c.a, b, "consultation", "adjacent", ev, 0.3 / c.gap));
      }
    }
  }

  // Rank + cap per target.
  const grouped = new Map();
  for (const e of edges) {
    const arr = grouped.get(e.target.id);
    if (arr) arr.push(e); else grouped.set(e.target.id, [e]);
  }
  const kept = [];
  for (const arr of grouped.values()) {
    arr.sort((x, y) => y.score - x.score);
    for (const e of arr.slice(0, MAX_EDGES_PER_TARGET)) kept.push(e);
  }
  kept.sort((x, y) => y.score - x.score);

  return {
    edges: kept,
    // The nodes the pass CONSIDERED, not just the ones that linked. A view can
    // only draw the unlinked half — which is most of a real window — if it can
    // see it; `stats.nodesByKind` counts them but names none, so a layout built
    // on edges alone silently redraws the same "everything here connected"
    // picture the census exists to contradict.
    nodes: list.map(compact),
    byTarget: index(kept, (e) => e.target.id),
    bySource: index(kept, (e) => e.source.id),
    stats: {
      nodes: list.length,
      edges: kept.length,
      derivation: kept.filter((e) => e.class === "derivation").length,
      consultation: kept.filter((e) => e.class === "consultation").length,
      // What the graph is actually MADE of, per kind. Without this a window
      // can't answer "is this extractor contributing at all?" — the failure
      // that hid an unwired node builder behind a graph that looked populated.
      nodesByKind: countBy(list, (n) => n.kind),
      // Kinds that reached an edge. A kind present in nodesByKind but missing
      // here is extracting fine and linking to nothing — a different problem
      // from not extracting at all, and worth being able to tell apart.
      linkedKinds: [...new Set(kept.flatMap((e) => [e.source.kind, e.target.kind]))].sort(),
    },
  };
}

const PRODUCTION_KINDS = new Set([
  "file.write", "file.edit", "linear.write", "forge.write", "query.write", "artifact.publish",
]);

// A lens's KIND cannot answer "was anything produced" — one `live` kind covers
// both a read-only probe and a rollout — so a lens-declared node is admitted by
// ROLE, which factoryNodes reads off that lens's own change vocabulary. An
// applied stack, an upload, a written secret and a changed service are all things
// something else can plausibly have fed; a `live` inspect or a `transfer`
// download produced nothing and stays a source.
//
// Keyed on `fromLens` rather than a list of kinds allowed to say "production":
// the list could only ever be as current as the last person to remember it, and
// a lens that declares its role correctly should not also need an entry here.
// The host kinds above stay a list because the host authors them.
function isProduction(n) {
  if (PRODUCTION_KINDS.has(n.kind)) return true;
  return Boolean(n.fromLens) && n.role === "production";
}

// Sources that inform by FORMING INTENT rather than by transferring text: web
// research, data queries, and prior-work recalls. When one of these precedes a
// production with no textual evidence, it stays a ranked consultation candidate
// — correlational, never labelled "derived from".
function isConsultative(n) {
  return n.kind.startsWith("web.") || n.kind.startsWith("query.") || n.kind.startsWith("recall.");
}

function edge(a, b, cls, tier, evidence, score) {
  return {
    source: compact(a),
    target: compact(b),
    direction: `${a.kind} → ${b.kind}`,
    class: cls,
    tier,
    evidence,
    score,
    sessionId: b.sessionId,
  };
}

function compact(n) {
  // sessionId travels because a LAYOUT needs per-node session, not just
  // per-edge: an unlinked node has no edge to read it off, and a view that
  // groups by session would silently drop exactly the nodes worth showing.
  return { id: n.id, key: n.key ?? null, kind: n.kind, ref: n.ref, label: n.label, ts: n.ts, promptId: n.promptId, endpointName: n.endpointName, intent: n.intent, sessionId: n.sessionId, role: n.role };
}

function pretty(e) {
  const i = e.indexOf(":");
  return i > 0 ? e.slice(i + 1) : e;
}

function countBy(items, keyFn) {
  const m = {};
  for (const it of items) {
    const k = keyFn(it);
    m[k] = (m[k] || 0) + 1;
  }
  return m;
}

function index(edges, keyFn) {
  const m = new Map();
  for (const e of edges) {
    const k = keyFn(e);
    const arr = m.get(k) || [];
    arr.push(e);
    m.set(k, arr);
  }
  return m;
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? 0 : p;
}
